const DEFAULTS = {
  apiBaseUrl: 'https://api.chiscari.ro',
  fallbackApiBaseUrl: 'https://oci.chiscari.ro',
  apiBaseUrls: ['https://api.chiscari.ro', 'https://oci.chiscari.ro'],
  photoGalleryUrl:
    'https://objectstorage.eu-frankfurt-1.oraclecloud.com/p/S6mq9J5Y9uoeqMFsLcsoDpBT0lydHMlJ1h-okE3lAit2xhYUkltwP9BefRgp018X/n/frwnfslc9lrx/b/photos/o/',
};

let config = null;
let activeApiBaseUrl = null;

function normalizeUrl(url) {
  return url ? url.replace(/\/+$/, '') : '';
}

function parseApiUrls(input) {
  if (!input) return [];
  if (Array.isArray(input)) return input.map(normalizeUrl).filter(Boolean);
  return input
    .split(',')
    .map(s => normalizeUrl(s.trim()))
    .filter(Boolean);
}

function getEnvFallbacks() {
  const customList = parseApiUrls(process.env.REACT_APP_API_BASE_URLS);
  const primary = normalizeUrl(process.env.REACT_APP_API_BASE_URL) || DEFAULTS.apiBaseUrl;
  const secondary = normalizeUrl(process.env.REACT_APP_FALLBACK_API_BASE_URL) || DEFAULTS.fallbackApiBaseUrl;
  const endpoints = Array.from(new Set([...customList, primary, secondary].filter(Boolean)));

  return {
    apiBaseUrl: primary,
    fallbackApiBaseUrl: secondary,
    apiBaseUrls: endpoints.length > 0 ? endpoints : DEFAULTS.apiBaseUrls,
    photoGalleryUrl: process.env.REACT_APP_PHOTO_GALLERY_URL || DEFAULTS.photoGalleryUrl,
  };
}

export async function loadConfig() {
  if (config) {
    return config;
  }

  const fallbacks = getEnvFallbacks();

  try {
    const response = await fetch(`${process.env.PUBLIC_URL || ''}/config.json`);
    if (response.ok) {
      const runtimeConfig = await response.json();
      const endpoints = Array.from(
        new Set([
          ...parseApiUrls(runtimeConfig.apiBaseUrls),
          normalizeUrl(runtimeConfig.apiBaseUrl),
          normalizeUrl(runtimeConfig.fallbackApiBaseUrl),
          ...fallbacks.apiBaseUrls,
        ].filter(Boolean))
      );

      config = {
        ...fallbacks,
        ...runtimeConfig,
        apiBaseUrl: normalizeUrl(runtimeConfig.apiBaseUrl) || fallbacks.apiBaseUrl,
        fallbackApiBaseUrl: normalizeUrl(runtimeConfig.fallbackApiBaseUrl) || fallbacks.fallbackApiBaseUrl,
        apiBaseUrls: endpoints.length > 0 ? endpoints : fallbacks.apiBaseUrls,
      };
    } else {
      config = fallbacks;
    }
  } catch {
    config = fallbacks;
  }

  return config;
}

export function getConfig() {
  if (!config) {
    config = getEnvFallbacks();
  }
  return config;
}

export function getApiEndpoints() {
  const currentConfig = getConfig();
  const list = currentConfig.apiBaseUrls || [currentConfig.apiBaseUrl, currentConfig.fallbackApiBaseUrl];
  return Array.from(new Set(list.map(normalizeUrl).filter(Boolean)));
}

export function getActiveApiBaseUrl() {
  if (activeApiBaseUrl) return activeApiBaseUrl;
  const endpoints = getApiEndpoints();
  return endpoints[0] || DEFAULTS.apiBaseUrl;
}

export function setActiveApiBaseUrl(url) {
  if (url) {
    activeApiBaseUrl = normalizeUrl(url);
  }
}

export function resetActiveApiBaseUrl() {
  activeApiBaseUrl = null;
}

/**
 * Intelligent fetch wrapper with automatic failover between api.chiscari.ro and oci.chiscari.ro.
 * If the primary endpoint fails or returns a 5xx server error, it automatically falls back to secondary endpoints.
 */
export async function apiFetch(endpointOrPath, options = {}) {
  const rawPath = endpointOrPath || '';
  const path = rawPath.startsWith('/') ? rawPath : `/${rawPath}`;
  const endpoints = getApiEndpoints();
  const currentActive = getActiveApiBaseUrl();

  const orderedEndpoints = [
    currentActive,
    ...endpoints.filter(e => e !== currentActive),
  ];

  let lastError = null;
  const timeoutMs = options.timeoutMs || 3500;

  for (let i = 0; i < orderedEndpoints.length; i++) {
    const baseUrl = orderedEndpoints[i];
    const url = `${baseUrl}${path === '/' ? '' : path}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, timeoutMs);

    try {
      const fetchOptions = {
        ...options,
        signal: options.signal || controller.signal,
      };

      const response = await fetch(url, fetchOptions);
      clearTimeout(timeoutId);

      // If server returned 5xx error and we have fallback endpoints, try next endpoint
      if (!response.ok && response.status >= 500 && i < orderedEndpoints.length - 1) {
        console.warn(`[apiFetch] Server error HTTP ${response.status} from ${baseUrl}${path}, attempting failover...`);
        continue;
      }

      // Successful request (2xx, 3xx, 4xx)
      setActiveApiBaseUrl(baseUrl);
      return response;
    } catch (err) {
      clearTimeout(timeoutId);
      lastError = err;
      const isTimeout = err.name === 'AbortError' || (err.message && err.message.includes('aborted'));
      console.warn(
        `[apiFetch] ${isTimeout ? `Timeout (>${timeoutMs}ms)` : 'Network error'} contacting ${baseUrl}${path}:`,
        err.message || err
      );
      if (i < orderedEndpoints.length - 1) {
        console.info(`[apiFetch] Failing over to ${orderedEndpoints[i + 1]}...`);
      }
    }
  }

  throw lastError || new Error(`Failed to fetch from all API endpoints: ${path}`);
}
